import * as React from 'react';
import { Fragment } from 'react';
import { compose, setDisplayName, withState, withHandlers } from 'recompose';
import { translate } from 'react-i18next';
import { TranslateProps } from 'util/i18n';
import { ReportSettings, ReportSettingsChangeHandler } from 'logic/reports/ReportSettings';
import * as moment from 'moment';
import { Input } from 'ui/shared/Input';
import { FadeCollapse } from 'ui/transitions';
import { hasFeature } from 'logic/reports/reportType';
import { TimeInterval } from 'ui/pages/designer/sections/schedule/TimeInterval';
import { ReportType } from 'types';

export type OwnProps = {
    reportSettings: Readonly<ReportSettings>;
    reportType: ReportType;
    onChange: ReportSettingsChangeHandler;
};

const styles = {
    indented: {
        marginLeft: '2em',
    },
    topMargin: {
        marginTop: '1em',
    },
};

const RawTimeRange = ({
    enabled,
    fromTime,
    toTime,
    handleChangeEnabled,
    handleChangeFromTime,
    handleChangeToTime,
    onChange,
    reportSettings,
    reportType,
    t,
}: OwnProps & RecomposeProps & TranslateProps) =>
    <Fragment>
        <div>
            <label>
                <input
                    type="checkbox"
                    id="edit-report-custom-time-range"
                    checked={ enabled }
                    onChange={ handleChangeEnabled }
                />
                { t('WC_REPORT_SCHEDULE_CUSTOM_TIME_RANGE') }
            </label>
        </div>
        <div style={ styles.indented }>
            <label htmlFor="edit-report-schedule-time-from">
                { t('262') }
            </label>
            <Input
                id="edit-report-schedule-time-from"
                value={ fromTime }
                onChange={ handleChangeFromTime }
                placeholder={ t('WC_REPORT_SCHEDULE_TIME_PLACEHOLDER') }
                maxLength={ 5 }
            />
            <label htmlFor="edit-report-schedule-time-to">
                { t('263') }
            </label>
            <Input
                id="edit-report-schedule-time-to"
                value={ toTime }
                onChange={ handleChangeToTime }
                placeholder={ t('WC_REPORT_SCHEDULE_TIME_PLACEHOLDER') }
                maxLength={ 5 }
            />
        </div>

        <FadeCollapse
            visible={ !hasFeature(reportType, 'noTimeInterval') }
            duration={ 200 }
            height="5em"
        >
            <div style={ styles.topMargin }>
                { t('6.1241.0.82') }
            </div>
            <div style={ styles.indented }>
                <div>
                    { t('WC_REPORT_TIME_INTERVAL_HINT') }
                </div>
                <TimeInterval reportSettings={ reportSettings } onChange={ onChange } />
            </div>
        </FadeCollapse>
    </Fragment>
;

type ChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => void;

type RecomposeProps = {
    enabled: boolean;
    fromTime: string;
    toTime: string;
    handleChangeEnabled: ChangeHandler;
    handleChangeFromTime: ChangeHandler;
    handleChangeToTime: ChangeHandler;
};
type _RecomposeProps = RecomposeProps & {
    _setEnabled: (b: boolean) => void;
    _setFromTime: (s: string) => void;
    _setToTime: (s: string) => void;
};

const isAllDay = (reportSettings: Readonly<ReportSettings>): boolean =>
    reportSettings.scheduleDataInclusionStart === 0 &&
    reportSettings.scheduleDataInclusionEnd === 1439
;

const timeNumberToString = (t: number): string =>
    moment.utc([ 2018 ]).startOf('day').add(t, 'minutes').format('HH:mm')
;

const bound = (min: number, value: number, max: number): number =>
    Math.max(min, Math.min(value, max))
;

const timeStringToNumber = (t: string): number =>
    bound(ReportSettings.defaultScheduleDataInclusionStart, moment.duration(t).asMinutes(), ReportSettings.defaultScheduleDataInclusionEnd)
;

const update = (props: OwnProps & _RecomposeProps) => {
    const { onChange, enabled } = props;
    onChange(reportSettings => {
        let fromTime = enabled && timeStringToNumber(props.fromTime) || ReportSettings.defaultScheduleDataInclusionStart;
        let toTime = enabled && timeStringToNumber(props.toTime) || ReportSettings.defaultScheduleDataInclusionEnd;
        if (fromTime > toTime) {
            // Force the from time to come before the to time.
            // Justin has said time range is only for within a day and that users can make their own
            // custom reports (outside of Symphony) if they want time ranges that cross over midnight.
            const temp = fromTime;
            fromTime = toTime;
            toTime = temp;
        }
        if (fromTime !== reportSettings.scheduleDataInclusionStart) {
            reportSettings = reportSettings.setScheduleDataInclusionStart(fromTime);
        }
        if (toTime !== reportSettings.scheduleDataInclusionEnd) {
            reportSettings = reportSettings.setScheduleDataInclusionEnd(toTime);
        }
        return reportSettings;
    });
};

// Ensure we've only got digits and colons in the time fields
const nonTimeCharsRegex = /[^\d:]/g;
const normalizeTime = (s: string): string =>
    s.replace(nonTimeCharsRegex, '')
;

export const TimeRange = compose<RecomposeProps & TranslateProps, OwnProps>(
    setDisplayName('RunOnScheduleTimeRange'),
    translate(),
    withState('enabled', '_setEnabled', ({ reportSettings }: OwnProps) => !isAllDay(reportSettings)),
    withState('fromTime', '_setFromTime', ({ reportSettings, enabled }: OwnProps & RecomposeProps) =>
        enabled
            ? timeNumberToString(reportSettings.scheduleDataInclusionStart)
            : ''
    ),
    withState('toTime', '_setToTime', ({ reportSettings, enabled }: OwnProps & RecomposeProps) =>
        enabled
            ? timeNumberToString(reportSettings.scheduleDataInclusionEnd)
            : ''
    ),
    withHandlers({
        handleChangeEnabled: (props: OwnProps & _RecomposeProps) => (e: React.ChangeEvent<HTMLInputElement>) => {
            const enabled = e.target.checked;
            props._setEnabled(enabled);

            const { _setFromTime, _setToTime } = props;
            let { fromTime, toTime } = props;
            if (!fromTime || !enabled) {
                fromTime = timeNumberToString(ReportSettings.defaultScheduleDataInclusionStart);
                _setFromTime(fromTime);
            }
            if (!toTime || !enabled) {
                toTime = timeNumberToString(ReportSettings.defaultScheduleDataInclusionEnd);
                _setToTime(toTime);
            }

            update({ ...props, enabled, fromTime, toTime });
        },
        handleChangeFromTime: (props: OwnProps & _RecomposeProps) => (e: React.ChangeEvent<HTMLInputElement>) => {
            const fromTime = normalizeTime(e.target.value);
            props._setFromTime(fromTime);
            props._setEnabled(true);
            update({ ...props, fromTime, enabled: true });
        },
        handleChangeToTime: (props: OwnProps & _RecomposeProps) => (e: React.ChangeEvent<HTMLInputElement>) => {
            const toTime = normalizeTime(e.target.value);
            props._setToTime(toTime);
            props._setEnabled(true);
            update({ ...props, toTime, enabled: true });
        },
    }),
)(RawTimeRange);



// WEBPACK FOOTER //
// ./src/ui/pages/designer/sections/dateRange/TimeRange.tsx